import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppointmentsDocumentsActions } from '../../../constants/actions';
import { AxiosError } from 'axios';
import { AppointmentDocumentsApi } from 'integration/api/documents';
import { DocumentUpdateMetadataDto } from '@docbay/schemas';

/**
 * Async thunk to post documents.
 * @param params - FormData containing the document data.
 * @returns The response data on success, or an error message on failure.
 */
export const postDocuments = createAsyncThunk(
  AppointmentsDocumentsActions.postDocuments,
  async (params: FormData, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await AppointmentDocumentsApi.postDocuments(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Async thunk to post documents to a specific appointment.
 * @param params - Object containing the appointmentId and document data as FormData.
 * @returns The response data on success, or an error message on failure.
 */
export const postDocumentsToAppointment = createAsyncThunk(
  AppointmentsDocumentsActions.postDocumentsToAppointment,
  async (
    params: {
      appointmentId: string;
      data: FormData;
    },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } =
        await AppointmentDocumentsApi.postDocumentsToAppointment(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Async thunk to download documents.
 * @param params - Object containing the bucketType and key for the document.
 * @returns The response data on success, or an error message on failure.
 */
export const downloadDocuments = createAsyncThunk(
  AppointmentsDocumentsActions.downloadDocuments,
  async (
    params: {
      bucketType: string;
      key: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } =
        await AppointmentDocumentsApi.downloadDocuments(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Async thunk to delete documents.
 * @param params - Object containing the relatedEntityId, bucketType, and key for the document.
 * @returns The response data on success, or an error message on failure.
 */
export const deleteDocuments = createAsyncThunk(
  AppointmentsDocumentsActions.deleteDocuments,
  async (
    params: {
      relatedEntityId?: string;
      bucketType: string;
      key: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } =
        await AppointmentDocumentsApi.deleteDocuments(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Async thunk to edit documents.
 * @param params - DocumentUpdateMetadataDto containing the updated document metadata.
 * @returns The response data on success, or an error message on failure.
 */
export const editDocuments = createAsyncThunk(
  AppointmentsDocumentsActions.editDocuments,
  async (params: DocumentUpdateMetadataDto, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await AppointmentDocumentsApi.editDocuments(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
